import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { UsageService } from '../shared/services/usage.service';
import { UsageScope, ledgerLabel } from '../shared/models/usage.model';
import { resetsAtLocal, resetsIn, resetsOn } from '../shared/utils/usage-reset';

/**
 * Compact "my usage" widget for the Studio shell avatar menu. Pure presentation
 * bound to UsageService signals — the service owns fetching/refresh.
 *
 * Ported from wg-agents-ui's usage-meter; adapted to studio-ui's design tokens
 * (--text / --text-3 / --surface-3 / --accent / --amber / --red). Both windows
 * (Today / This month) show a level-coloured bar: brand red (ok) → amber
 * (approaching ≥80%) → red (reached ≥100%). The per-ledger breakdown emphasises
 * the BUILDER ("Building agents (studio)") ledger, which is studio build-time AI.
 */
@Component({
  selector: 'app-usage-meter',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!unlimited()) {
    <div class="usage-meter" role="group" aria-label="Token usage">
      <div class="usage-meter__head">Usage</div>
      @if (loading() && !usage()) {
        <span class="usage-meter__muted">Loading usage…</span>
      } @else if (errored() && !usage()) {
        <span class="usage-meter__muted">Usage unavailable</span>
      } @else if (usage(); as u) {
        <!-- PER-LEDGER is primary: quotas are enforced independently per ledger
             ({cost_center}#{username}), and Studio's build-time AI spends the
             BUILDER ledger — so that's the row a builder is actually gated on.
             The combined Today / This month totals below are informational only
             (NOT an enforced cap). Show the breakdown only when the API returns
             ledgers (graceful for older API responses). -->
        @if (u.ledgers && u.ledgers.length > 0) {
          <div class="usage-meter__head">By ledger · this month</div>
          @for (lg of u.ledgers; track lg.cost_center) {
            <div
              class="usage-meter__row usage-meter__row--ledger"
              [class.usage-meter__row--builder]="lg.cost_center === 'BUILDER'"
            >
              <span class="usage-meter__label">{{ ledgerLabel(lg.cost_center) }}</span>
              @if (lg.unlimited) {
                <span class="usage-meter__val">{{ fmt(lg.monthly.used_tokens) }} · Unlimited</span>
              } @else {
                <span class="usage-meter__val">{{ fmt(lg.monthly.used_tokens) }} / {{ fmt(lg.monthly.limit_tokens ?? 0) }}</span>
              }
            </div>
            @if (!lg.unlimited) {
              <div class="usage-meter__bar">
                <div
                  class="usage-meter__fill"
                  [class]="'usage-meter__fill--' + level(lg.monthly, lg.unlimited)"
                  [style.width.%]="pct(lg.monthly)"
                ></div>
              </div>
            }
          }
        }

        <!-- Total across ALL ledgers — INFORMATIONAL only, not an enforced cap.
             Enforcement is per-ledger (above); this is just the aggregate spend,
             so the bars are rendered neutral (no amber/red alarm colours) to
             avoid reading as a limit you can hit. -->
        <div
          class="usage-meter__head usage-meter__head--sub"
          [class.usage-meter__head--total-first]="!(u.ledgers && u.ledgers.length > 0)"
        >Total (all ledgers) · informational</div>
        <div class="usage-meter__row">
          <span class="usage-meter__label">Today</span>
          <span class="usage-meter__val">{{ fmt(u.daily.used_tokens) }} / {{ fmt(u.daily.limit_tokens ?? 0) }}</span>
        </div>
        <div
          class="usage-meter__bar"
          role="progressbar"
          [attr.aria-valuenow]="dailyPct()"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuetext]="dailyPct() + '% of today\\'s combined tokens (informational)'"
        >
          <div
            class="usage-meter__fill usage-meter__fill--total"
            [style.width.%]="dailyPct()"
          ></div>
        </div>
        @if (u.daily.resets_at) {
          <div class="usage-meter__reset" [title]="resetsAtLocal(u.daily.resets_at)">
            Resets {{ resetsIn(u.daily.resets_at) }}
          </div>
        }

        <div class="usage-meter__row usage-meter__row--sub">
          <span class="usage-meter__label">This month</span>
          <span class="usage-meter__val">{{ fmt(u.monthly.used_tokens) }} / {{ fmt(u.monthly.limit_tokens ?? 0) }}</span>
        </div>
        <div
          class="usage-meter__bar"
          role="progressbar"
          [attr.aria-valuenow]="monthlyPct()"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuetext]="monthlyPct() + '% of this month\\'s combined tokens (informational)'"
        >
          <div
            class="usage-meter__fill usage-meter__fill--total"
            [style.width.%]="monthlyPct()"
          ></div>
        </div>
        @if (u.monthly.resets_at) {
          <div class="usage-meter__reset" [title]="resetsAtLocal(u.monthly.resets_at)">
            Resets {{ resetsOn(u.monthly.resets_at) }}
          </div>
        }
      }
    </div>
    }
  `,
  styles: [`
    .usage-meter { display: flex; flex-direction: column; gap: 4px; padding: 4px 2px 2px; font-size: 12px; }
    .usage-meter__head {
      font-size: 0.62rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
      color: var(--text-3, #8b91a3); margin-bottom: 2px;
    }
    .usage-meter__row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
    .usage-meter__row--sub { margin-top: 7px; }
    .usage-meter__head--sub { margin-top: 11px; }
    /* When there are no ledgers the Total block leads — drop the top margin so it
       doesn't float below the "Usage" header. */
    .usage-meter__head--total-first { margin-top: 0; }
    .usage-meter__row--ledger { margin-top: 5px; }
    /* Emphasise the BUILDER ledger — that's the studio build-time AI spend. */
    .usage-meter__row--builder .usage-meter__label { color: var(--text, #191c22); font-weight: 650; }
    .usage-meter__label { color: var(--text-3, #6b7280); }
    .usage-meter__val { color: var(--text, #191c22); font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .usage-meter__muted { color: var(--text-3, #6b7280); }
    /* Muted "Resets …" hint under each total window (daily countdown / monthly date). */
    .usage-meter__reset { margin-top: 3px; font-size: 0.68rem; line-height: 1.3; color: var(--text-3, #8b91a3); }
    .usage-meter__bar { height: 5px; border-radius: 999px; background: var(--surface-3, #e9ebee); overflow: hidden; }
    /* Studio palette: brand red (ok) -> amber (approaching) -> red (reached). */
    .usage-meter__fill { height: 100%; border-radius: 999px; transition: width 0.3s ease, background 0.2s ease; background: var(--accent, #d81e2c); }
    .usage-meter__fill--approaching { background: var(--amber, #a86a10); }
    .usage-meter__fill--reached { background: var(--red, #c92333); }
    /* The combined "Total (all ledgers)" bar is informational, NOT a cap, so it
       stays a neutral grey — never amber/red — to avoid implying a limit. */
    .usage-meter__fill--total { background: var(--text-3, #8b91a3); }
  `],
})
export class UsageMeterComponent {
  private readonly svc = inject(UsageService);
  readonly usage = this.svc.usage;
  readonly loading = this.svc.loading;
  readonly errored = this.svc.errored;
  readonly unlimited = this.svc.unlimited;
  // The combined Today / This month bars are informational now (neutral colour),
  // so only the clamped percentages are needed — not the alarm level signals.
  readonly dailyPct = this.svc.dailyPct;
  readonly monthlyPct = this.svc.monthlyPct;

  private readonly _fmt = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
  fmt = (n: number): string => this._fmt.format(n ?? 0);

  ledgerLabel = ledgerLabel;

  // "Resets …" hint formatters — framework-free, unit-tested in usage-reset.spec.
  // Daily is a countdown ("in 6h"), monthly a local date ("Aug 1"); the exact
  // local datetime is the hover tooltip. See usage-reset.ts for the tz rationale.
  resetsIn = resetsIn;
  resetsOn = resetsOn;
  resetsAtLocal = resetsAtLocal;

  /** Clamped 0–100 fill for a ledger scope's bar. */
  pct(scope: UsageScope): number {
    return Math.max(0, Math.min(100, Math.round(scope?.pct_used ?? 0)));
  }

  /** Bar colour level for a ledger scope — mirrors UsageService.levelFor
   *  (≥100 reached, ≥80 approaching, else ok). */
  level(scope: UsageScope, unlimited: boolean): 'ok' | 'approaching' | 'reached' {
    const p = scope?.pct_used;
    if (unlimited || p == null) return 'ok';
    if (p >= 100) return 'reached';
    if (p >= 80) return 'approaching';
    return 'ok';
  }
}
